import { useState } from "react";
import { toast, Toaster } from "sonner";
import { Loader2, Play, Sparkles } from "lucide-react";
import { generateClips, saveClip, rewriteClip, deleteClip } from "../lib/api";
import ClipCard from "../components/ClipCard";

const PLATFORMS = ["tiktok", "reels", "shorts"];
const THEMES = [
    "workplace", "dating", "economy", "ai", "housing", "healthcare",
    "loneliness", "social_media", "institutional_trust", "existential_absurdity",
];
const HUMOR_MODES = [
    "absurd", "data_backed", "social_commentary", "mock_psa",
    "workplace_memo", "therapy_session", "ai_confusion", "customer_service_nightmare",
];
const CHAR_FORMATS = ["monologue", "two_person_skit", "narrator", "fake_ad", "fake_psa"];
const RISK = ["conservative", "standard", "edgy_but_safe"];
const COUNTS = [1, 3, 5, 10];

const Field = ({ label, children }) => (
    <div className="space-y-2">
        <label className="d8-label block">{label}</label>
        {children}
    </div>
);

const Select = ({ value, onChange, options, testid, format }) => (
    <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-sm uppercase tracking-wider p-3 focus:outline-none focus:border-[#ff2a2a]"
    >
        {options.map((o) => (
            <option key={o} value={o}>
                {format ? format(o) : String(o).replace(/_/g, " ")}
            </option>
        ))}
    </select>
);

export const Generator = () => {
    const [form, setForm] = useState({
        platform: "tiktok",
        theme: "workplace",
        humor_mode: "absurd",
        darkness_level: 3,
        count: 3,
        character_format: "monologue",
        risk_tolerance: "standard",
        data_backed: false,
        custom_prompt: "",
    });
    const [loading, setLoading] = useState(false);
    const [clips, setClips] = useState([]);
    const [rewritingId, setRewritingId] = useState(null);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const run = async () => {
        setLoading(true);
        try {
            const data = await generateClips(form);
            setClips(data.clips || []);
            toast.success(`Generated ${data.clips?.length || 0} clip(s)`, {
                description: "8.00s each. Safety scored. Awaiting your call.",
            });
        } catch (e) {
            console.error(e);
            toast.error("Generation failed", {
                description: e?.response?.data?.detail || e.message,
            });
        } finally {
            setLoading(false);
        }
    };

    const onSave = async (clip) => {
        try {
            await saveClip(clip.id, !clip.saved);
            setClips((arr) => arr.map((c) => (c.id === clip.id ? { ...c, saved: !c.saved } : c)));
            toast.success(clip.saved ? "Removed from library" : "Saved to library");
        } catch (e) {
            toast.error("Save failed");
        }
    };

    const onRewrite = async (clip) => {
        setRewritingId(clip.id);
        try {
            const updated = await rewriteClip(clip.id);
            setClips((arr) => arr.map((c) => (c.id === clip.id ? updated : c)));
            toast.success("Rewritten", { description: "Risk lowered, joke preserved." });
        } catch (e) {
            toast.error("Rewrite failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setRewritingId(null);
        }
    };

    const onDelete = async (clip) => {
        try {
            await deleteClip(clip.id);
            setClips((arr) => arr.filter((c) => c.id !== clip.id));
            toast("Deleted");
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="generator-page" className="min-h-screen">
            <Toaster theme="dark" position="top-right" />
            <section className="border-b border-[#262626] px-6 md:px-12 py-10 noise-bg">
                <div className="d8-label mb-3">/ Generator</div>
                <h1 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-5xl leading-none">
                    Engineer a reaction.
                </h1>
                <p className="mt-4 text-[#8a8a8a] max-w-2xl">
                    Every output is exactly 8.00 seconds. Hook / Misdirection / Twist. Safety-scored.
                    Choose the pressure, we'll compress the joke.
                </p>
            </section>

            <div className="grid lg:grid-cols-[380px_1fr] gap-px bg-[#262626]">
                {/* Controls */}
                <aside className="bg-[#050505] p-6 md:p-8 space-y-5 lg:sticky lg:top-14 lg:self-start lg:max-h-[calc(100vh-3.5rem)] lg:overflow-y-auto">
                    <Field label="Platform">
                        <Select
                            testid="field-platform"
                            value={form.platform}
                            onChange={(v) => setField("platform", v)}
                            options={PLATFORMS}
                        />
                    </Field>
                    <Field label="Theme">
                        <Select
                            testid="field-theme"
                            value={form.theme}
                            onChange={(v) => setField("theme", v)}
                            options={THEMES}
                        />
                    </Field>
                    <Field label="Humor mode">
                        <Select
                            testid="field-humor-mode"
                            value={form.humor_mode}
                            onChange={(v) => setField("humor_mode", v)}
                            options={HUMOR_MODES}
                        />
                    </Field>
                    <Field label="Character format">
                        <Select
                            testid="field-character"
                            value={form.character_format}
                            onChange={(v) => setField("character_format", v)}
                            options={CHAR_FORMATS}
                        />
                    </Field>
                    <Field label="Risk tolerance">
                        <Select
                            testid="field-risk"
                            value={form.risk_tolerance}
                            onChange={(v) => setField("risk_tolerance", v)}
                            options={RISK}
                        />
                    </Field>

                    <Field label={`Darkness level · ${form.darkness_level}/5`}>
                        <div className="flex gap-px bg-[#262626] border border-[#262626]">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                    key={n}
                                    data-testid={`darkness-${n}`}
                                    onClick={() => setField("darkness_level", n)}
                                    className={`flex-1 py-3 font-mono-d8 text-xs uppercase tracking-wider transition-colors ${
                                        form.darkness_level === n
                                            ? "bg-[#ff2a2a] text-[#f5f5f0]"
                                            : "bg-[#050505] text-[#8a8a8a] hover:bg-[#121212]"
                                    }`}
                                >
                                    D{n}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <Field label="Output count">
                        <div className="flex gap-px bg-[#262626] border border-[#262626]">
                            {COUNTS.map((n) => (
                                <button
                                    key={n}
                                    data-testid={`count-${n}`}
                                    onClick={() => setField("count", n)}
                                    className={`flex-1 py-3 font-mono-d8 text-xs uppercase tracking-wider transition-colors ${
                                        form.count === n
                                            ? "bg-[#f5f5f0] text-[#050505]"
                                            : "bg-[#050505] text-[#8a8a8a] hover:bg-[#121212]"
                                    }`}
                                >
                                    {n}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <label className="flex items-center justify-between gap-3 border border-[#262626] p-3 cursor-pointer hover:bg-[#121212] transition-colors">
                        <div>
                            <div className="d8-label">Data-backed mode</div>
                            <div className="font-mono-d8 text-[10px] text-[#8a8a8a] mt-1">
                                themes only · no fake stats
                            </div>
                        </div>
                        <input
                            data-testid="field-data-backed"
                            type="checkbox"
                            checked={form.data_backed}
                            onChange={(e) => setField("data_backed", e.target.checked)}
                            className="w-4 h-4 accent-[#ff2a2a]"
                        />
                    </label>

                    <Field label="Custom guidance (optional)">
                        <textarea
                            data-testid="field-custom"
                            value={form.custom_prompt}
                            onChange={(e) => setField("custom_prompt", e.target.value)}
                            rows={3}
                            placeholder="e.g. set in a DMV, use deadpan voiceover"
                            className="w-full bg-[#050505] border border-[#262626] text-[#f5f5f0] font-mono-d8 text-xs p-3 focus:outline-none focus:border-[#ff2a2a]"
                        />
                    </Field>

                    <button
                        data-testid="generate-button"
                        onClick={run}
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-3 bg-[#ff2a2a] hover:bg-[#cc1a1a] disabled:opacity-60 disabled:cursor-not-allowed text-[#f5f5f0] font-heading font-bold uppercase tracking-widest py-4 transition-colors"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Compressing 8.00s
                            </>
                        ) : (
                            <>
                                <Sparkles className="w-4 h-4" />
                                Generate
                            </>
                        )}
                    </button>
                </aside>

                {/* Output column */}
                <main className="bg-[#050505] p-6 md:p-10 min-h-[60vh]">
                    {clips.length === 0 && !loading && (
                        <div
                            data-testid="empty-state"
                            className="flex flex-col items-center justify-center text-center py-24 border border-dashed border-[#262626]"
                        >
                            <Play className="w-10 h-10 text-[#262626] mb-4" strokeWidth={1} />
                            <div className="d8-label mb-2">Awaiting signal</div>
                            <p className="text-[#8a8a8a] text-sm max-w-md">
                                Configure the control panel and hit Generate.
                                Output arrives in discrete 8-second blocks.
                            </p>
                        </div>
                    )}

                    {loading && clips.length === 0 && (
                        <div
                            data-testid="loading-state"
                            className="flex flex-col items-center justify-center text-center py-24 border border-[#262626] bg-[#0a0a0a]"
                        >
                            <Loader2 className="w-8 h-8 text-[#ff2a2a] animate-spin mb-4" />
                            <div className="d8-label text-[#f5f5f0]">Engineering reaction sequence…</div>
                            <p className="font-mono-d8 text-xs text-[#8a8a8a] mt-2">
                                Structuring hook · compressing misdirection · sharpening twist
                            </p>
                        </div>
                    )}

                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
                        {clips.map((c) => (
                            <ClipCard
                                key={c.id}
                                clip={c}
                                onSave={onSave}
                                onRewrite={onRewrite}
                                onDelete={onDelete}
                                rewriting={rewritingId === c.id}
                            />
                        ))}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default Generator;
